// ============================================================
// A HIT'S MOMENTS, DRAWN (plan body-map H2; engine/plate/moments): at each
// struck place, in the burst's turn, what the hit met - a force field's
// block (the shield's patch lit from the impact outward, an impact ring, a
// flash; an overload shatters it) or Daemonic protection's soak (the warp's
// ink blooming on the wound, the light streaming into it, the lines closing
// over). Drawn once when the burst arrives, each at its hit's moment (CSS
// delays), inside the figure so it moves with it. Motion off: each holds
// as a brief still flash.
// ============================================================

import { memo } from 'react';
import clsx from '../../../utils/clsx';
import { blockCells, hexPoints, swallowOf, type Guard } from '../../../engine/plate/moments';
import type { BurstHit } from '../../../engine/plate/burst';
import type { PlateFrame, Point } from '../../../engine/plate/frame';
import fx from '../../../styles/PlateFx.module.css';
import tx from '../../../styles/PlateTraits.module.css';
import { bypassOf, crackPath } from '../../../engine/plate/daemonic';
import type { HitPlay } from '../usePlateHits';
import SpecialMoments from './specials/SpecialMoments';
import { timed } from './timing';

/** The shield at the point of impact: its patch of cells, an impact ring, a flash; shattered on an overload. */
function FieldBlock({ at, hit, rating, k }: { at: Point; hit: BurstHit; rating: number; k: number }) {
  const [x, y] = at;
  return (
    <g data-moment="block" data-overloaded={hit.overloaded || undefined}>
      {blockCells(at, rating, k).map((c, i) => (
        <polygon
          key={i}
          className={clsx(fx.fieldCell, hit.overloaded && fx.fieldOver)}
          points={hexPoints(c)}
          style={timed(hit.at + c.delay, 0, { '--fx': Math.round(c.x - x), '--fy': Math.round(c.y - y) })}
        />
      ))}
      {[0, 1, 2].map((n) => (
        <circle
          key={n}
          className={fx.impactRing}
          cx={x}
          cy={y}
          r={600 * k}
          strokeWidth={(26 - 6 * n) * k}
          style={timed(hit.at + n * 0.16)}
        />
      ))}
      <circle className={fx.impactFlash} cx={x} cy={y} r={60 * k} style={timed(hit.at)} />
    </g>
  );
}

/** The warp swallows the blow: its ink blooms on the wound, the light streams in, the lines close over it. */
interface SwallowProps {
  u: string;
  at: Point;
  hit: BurstHit;
  X: number;
  k: number;
  lines: boolean;
  /** The impact's and the hit's: the streaks fall the same on every screen. */
  seed: string;
}

function Swallow({ u, at, hit, X, k, lines, seed }: SwallowProps) {
  const [x, y] = at;
  const { radius, streaks } = swallowOf(at, X, seed, k);
  const clip = `${u}-soak${Math.round(hit.at * 100)}`;
  return (
    <g data-moment="soak">
      <circle className={fx.inkBloom} cx={x} cy={y} r={radius} filter={`url(#${u}-ink)`} style={timed(hit.at)} />
      <circle className={fx.inkHalo} cx={x} cy={y} r={radius * 1.25} filter={`url(#${u}-ink)`} style={timed(hit.at)} />
      {streaks.map((s, i) => (
        <line
          key={i}
          className={fx.soakStreak}
          x1={s.x}
          y1={s.y}
          x2={s.x + 90 * k * s.cos}
          y2={s.y + 90 * k * s.sin}
          strokeWidth={(5 + (i % 3) * 2) * k}
          style={timed(hit.at + s.delay, 0, { '--tx': Math.round(-s.run * s.cos), '--ty': Math.round(-s.run * s.sin) })}
        />
      ))}
      <circle className={fx.soakLight} cx={x} cy={y} r={110 * k} style={timed(hit.at)} />
      {lines && (
        <>
          <clipPath id={clip}>
            <circle cx={x} cy={y} r={radius} />
          </clipPath>
          <g
            className={fx.soakDark}
            clipPath={`url(#${clip})`}
            filter={`url(#${u}-ink)`}
            style={timed(hit.at + 0.35, 0, { '--soak-w': 50 + 6 * X })}
          >
            <use href={`#${u}-art`} fill="currentColor" />
          </g>
        </>
      )}
    </g>
  );
}

/** A holy, psychic or warp hit cracks through Daemonic protection in its own light (plan body-map T1). */
const CRACK = { holy: tx.crackHoly, force: tx.crackForce, warp: tx.crackWarp };

interface MomentsProps {
  u: string;
  play: HitPlay;
  /** Where each hit is centred, the plate's scale, and whether the lines close over a wound (not on the goo). */
  frame: PlateFrame;
  /** The body's force field (its patch's density) and Daemonic protection (X; 0 for none). */
  guard: Guard;
}

/** Every hit's moment in the burst - its block or its soak - at its place, in its turn. */
function HitMoments({ u, play, frame, guard }: MomentsProps) {
  const { anchors, k, lines } = frame;
  const { rating, daemonic } = guard;
  const at = (h: BurstHit) => anchors[h.location] ?? anchors.body ?? null;
  const blocks = play.burst.hits.filter((h) => h.blocked && at(h));
  const soaks = daemonic > 0 ? play.burst.hits.filter((h) => !h.blocked && h.soaked && at(h)) : [];
  const light = daemonic > 0 ? bypassOf(play.burst.specials) : null;
  const cracks = light ? play.burst.hits.filter((h) => !h.blocked && !h.soaked && at(h)) : [];
  if (!blocks.length && !soaks.length && !cracks.length) return null;
  return (
    <g key={play.id} pointerEvents="none" data-testid="plate-moments">
      {soaks.length > 0 && (
        <filter id={`${u}-ink`} x="-60%" y="-60%" width="220%" height="220%">
          <feTurbulence type="fractalNoise" baseFrequency={0.018 / k} numOctaves={3} seed={8} result="n" />
          <feDisplacementMap
            in="SourceGraphic"
            in2="n"
            scale={140 * k}
            xChannelSelector="R"
            yChannelSelector="G"
            result="d"
          />
          <feGaussianBlur in="d" stdDeviation={16 * k} />
        </filter>
      )}
      {blocks.map((h, i) => (
        <FieldBlock key={`b${i}`} at={at(h)!} hit={h} rating={rating} k={k} />
      ))}
      {soaks.map((h, i) => (
        <Swallow key={`s${i}`} u={u} at={at(h)!} hit={h} X={daemonic} k={k} lines={lines} seed={`${play.id}:${h.at}`} />
      ))}
      {cracks.length > 0 && (
        <filter id={`${u}-crackGlow`} x="-50%" y="-50%" width="200%" height="200%">
          <feGaussianBlur stdDeviation={10 * k} result="b" />
          <feMerge>
            <feMergeNode in="b" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
      )}
      {cracks.map((h, i) => (
        <path
          key={`c${i}`}
          className={clsx(tx.crack, CRACK[light!])}
          d={crackPath(at(h)!, `${play.id}:${h.at}`)}
          filter={`url(#${u}-crackGlow)`}
          style={timed(h.at)}
          data-moment="cracked"
        />
      ))}
    </g>
  );
}

/**
 * What each hit met and what the weapon's specials did, at each struck place in turn - inside the figure, so they move
 * with it. Memoised: its props are fixed for a burst, so the burst's turns (each a render of the plate) pass it by.
 */
export const StrikeMoments = memo(function StrikeMoments(props: MomentsProps) {
  return (
    <>
      <HitMoments {...props} />
      <SpecialMoments {...props} />
    </>
  );
});
